/**
 * useCogoWebMCP — high-level hook for integrating WebMCP into a Cogo app.
 *
 * Usage in App.tsx:
 * ```tsx
 * const { registry, onRuntimeReady, toolCount } = useCogoWebMCP()
 * // Pass onRuntimeReady to <CogoApp onRuntimeReady={onRuntimeReady} />
 * ```
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { CogoToolRegistry } from "../core/tool-registry"
import {
  createWebMCPIntegration,
  type CogoRuntimeLike,
  type WebMCPOptions,
} from "./renderer-plugin"

export interface UseCogoWebMCPResult {
  /** The tool registry (for <WebMCPProvider>) */
  registry: CogoToolRegistry
  /** Callback for CogoApp's onRuntimeReady prop */
  onRuntimeReady: (runtime: CogoRuntimeLike) => void
  /** Current number of registered tools (reactive) */
  toolCount: number
}

export function useCogoWebMCP(options?: WebMCPOptions): UseCogoWebMCPResult {
  const [toolCount, setToolCount] = useState(0)
  const optionsRef = useRef(options)
  optionsRef.current = options

  // Create integration once on mount
  const integration = useMemo(
    () => createWebMCPIntegration(optionsRef.current),
    [],
  )

  // Subscribe to tool count changes
  useEffect(() => {
    const unsub = integration.registry.onToolsChanged((tools) => {
      setToolCount(tools.length)
    })
    // Sync initial count
    setToolCount(integration.registry.size)
    return unsub
  }, [integration])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      integration.dispose()
    }
  }, [integration])

  // Stable callback for onRuntimeReady
  const onRuntimeReady = useCallback(
    (runtime: CogoRuntimeLike) => {
      integration.onRuntimeReady(runtime)
    },
    [integration],
  )

  return {
    registry: integration.registry,
    onRuntimeReady,
    toolCount,
  }
}
